import { z } from 'zod'
import { scopeLabel } from './smsJournal'

/**
 * Bibliothèque de modèles SMS (`sms_templates_v1`). Chaque modification crée une version immuable ; un envoi garde le
 * texte rendu et la version réellement envoyés. Les variables d'un modèle sont celles que fournit le module appelant :
 * l'écran ne permet pas d'en inventer.
 */

export const templateSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  scope: z.string(),
  module: z.string(),
  language: z.string(),
  isActive: z.boolean(),
  isSystem: z.boolean(),
  usageNote: z.string().nullable(),
  version: z.number().int(),
  body: z.string(),
  allowedVariables: z.array(z.string()),
  authorName: z.string().nullable(),
  versionCreatedAt: z.string(),
  sentCount: z.number().int(),
})
export type SmsTemplate = z.infer<typeof templateSchema>
export const templateListSchema = z.array(templateSchema)

export const templateVersionsSchema = z.array(
  z.object({
    version: z.number().int(),
    body: z.string(),
    authorName: z.string().nullable(),
    createdAt: z.string(),
    sentCount: z.number().int(),
  }),
)
export type SmsTemplateVersion = z.infer<typeof templateVersionsSchema>[number]

export const replyTemplatesSchema = z.array(
  z.object({ id: z.string(), name: z.string(), body: z.string(), allowedVariables: z.array(z.string()) }),
)
export type ReplyTemplate = z.infer<typeof replyTemplatesSchema>[number]

const moduleCategories: Record<string, string> = {
  CONTRACTS: 'Contrats',
  BILLING: 'Facturation',
  OPERATOR: 'Opérateur',
  MISSIONS: 'Missions',
}

/** Catégorie affichée : module appelant ; le module SMS se partage entre Opérations et Système. */
export function templateCategory(template: Pick<SmsTemplate, 'module' | 'scope' | 'isSystem'>): string {
  if (template.isSystem) return 'Système'
  return moduleCategories[template.module] ?? (template.scope === 'SNOW_ALERTS' ? 'Opérations' : 'Système')
}

export function templateScopeLabel(scope: string): string {
  return scope === 'CONVERSATION' ? 'Conversation' : scopeLabel(scope)
}

/**
 * Valeurs d'exemple de l'aperçu, affichées comme telles (« valeurs d'exemple ») : elles ne décrivent aucun client et
 * servent à estimer la longueur réelle du message.
 */
const variableExamples: Record<string, string> = {
  date: '18 nov.',
  heure: '6 h',
  heure_retour: '9 h',
  adresse: '214, rue des Érables',
  contrat: 'C-2026-0418',
  lien_signature: 'https://groupereca.com/p/AbCdEf12',
  montant: '425,00 $',
  lien_recu: 'https://admin.groupereca.com/r/AbCdEf12',
  facture: 'F-10872',
  echeance: '1er déc.',
  essai: 'essai 1',
  texte: 'Réponse de l’agent',
}

export function variableExample(name: string): string {
  return variableExamples[name] ?? `[${name}]`
}

/** Variables écrites dans un texte, dans l'ordre d'apparition, sans doublon. */
export function templateVariables(body: string): string[] {
  const names = [...body.matchAll(/\{\{\s*([^}\s]*)\s*\}\}/g)].map((match) => match[1] ?? '')
  return [...new Set(names)]
}

export function renderExample(body: string): string {
  return body.replace(/\{\{\s*([^}\s]*)\s*\}\}/g, (_whole, name: string) => variableExample(name))
}

/** Même contrôle que le serveur, avant l'appel : variable non prévue, mention STOP, longueur. */
export function templateError(body: string, allowed: readonly string[], scope: string): string | null {
  const text = body.trim()
  if (!text) return 'Le texte ne peut pas être vide.'
  if (text.length > 1600) return 'Texte trop long : 1600 caractères au plus.'
  const unknown = templateVariables(text).filter((name) => !allowed.includes(name))
  if (unknown.length > 0) {
    return `Variable non autorisée pour ce modèle : ${unknown.map((name) => `{{${name}}}`).join(', ')}`
  }
  if ((scope === 'SNOW_ALERTS' || scope === 'ADMINISTRATIVE') && !text.includes('STOP')) {
    return 'Mention de désinscription manquante : ajouter « STOP pour vous désinscrire ».'
  }
  return null
}

const gsmBasic =
  '@£$¥èéùìòÇ\nØø\rÅåΔ_ΦΓΛΩΠΨΣΘΞÆæßÉ !"#¤%&\'()*+,-./0123456789:;<=>?¡ABCDEFGHIJKLMNOPQRSTUVWXYZÄÖÑÜ§¿abcdefghijklmnopqrstuvwxyzäöñüà'
const gsmExtension = '^{}\\[~]|€\f'

/** Caractères hors GSM-7 : chacun fait passer le message à 70 caractères par segment. */
export function nonGsmCharacters(text: string): string[] {
  return [
    ...new Set(Array.from(text).filter((char) => !gsmBasic.includes(char) && !gsmExtension.includes(char))),
  ]
}

/** Variables encore présentes dans un brouillon de réponse : à remplacer avant l'envoi. */
export function unfilledVariables(draft: string): string[] {
  return templateVariables(draft)
}
